import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import '../styles/InvoiceSettingDrawer.css';

/* ───────────── date helpers ───────────── */
const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseISO = (s) => {
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const fmtLong = (iso) => {
  if (!iso) return '—';
  const d = parseISO(iso);
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtShort = (iso) =>
  parseISO(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
const money = (n) =>
  `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function addInterval(startISO, i, step, unit) {
  const s = parseISO(startISO);
  const n = i * step;
  if (unit === 'months') {
    const target = new Date(s.getFullYear(), s.getMonth() + n, 1);
    const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    target.setDate(Math.min(s.getDate(), last));
    return target;
  }
  const days = unit === 'weeks' ? n * 7 : n;
  return new Date(s.getFullYear(), s.getMonth(), s.getDate() + days);
}

/** Ongoing projects (no end date) show this many months ahead of today. */
const ONGOING_MONTHS = 12;

export function buildSchedule({ startDate, endDate, ongoing, frequency, every, unit }) {
  if (ongoing && !endDate) {
    const d = new Date();
    endDate = toISO(new Date(d.getFullYear(), d.getMonth() + ONGOING_MONTHS, d.getDate()));
  }
  if (!startDate || !endDate) return [];
  let step = 1;
  let u = 'days';
  if (frequency === 'weekly') { step = 7; u = 'days'; }
  else if (frequency === 'fortnightly') { step = 14; u = 'days'; }
  else if (frequency === 'monthly') { step = 1; u = 'months'; }
  else { step = Math.max(1, Number(every) || 1); u = unit || 'days'; }
  const end = parseISO(endDate);
  const out = [];
  for (let i = 0; i < 600; i += 1) {
    const d = addInterval(startDate, i, step, u);
    if (d > end) break;
    out.push(toISO(d));
  }
  return out;
}

/* ───────────── invoice preview ───────────── */
function InvoicePreview({ data, invoiceDate, dueDays, serviceFrom, serviceTo, overrideTotal, onEdit, onAddLine }) {
  const { company = {}, client = {}, lines = [], gstRate = 0.1, bank = {}, invoiceNo = 'INV—' } = data;
  const subtotal = lines.reduce((s, l) => s + Number(l.amount || 0), 0);
  const gst = subtotal * gstRate;
  const computed = subtotal + gst;
  const total = overrideTotal != null && overrideTotal !== '' ? Number(overrideTotal) : computed;
  const dueISO = invoiceDate ? toISO(new Date(parseISO(invoiceDate).getTime() + dueDays * 86400000)) : '';

  return (
    <div className="isd-preview-wrap">
      <div className="isd-section-title">
        <span>{onEdit ? 'Invoice preview' : 'Invoice template'}</span>
      </div>
      <div className="isd-invoice">
        <div className="isd-inv-head">
          <div>
            <h2>INVOICE</h2>
            <p className="isd-muted">{company.name || 'Company'} · {invoiceNo}</p>
          </div>
          <dl className="isd-inv-meta">
            <div><dt>Invoice No:</dt><dd>{invoiceNo}</dd></div>
            <div><dt>Invoice Date:</dt><dd>{fmtLong(invoiceDate)}</dd></div>
            <div><dt>Due Date:</dt><dd>{fmtLong(dueISO)}</dd></div>
            {!serviceFrom || !serviceTo || serviceTo === serviceFrom ? (
              <div>
                <dt>Service Date:</dt>
                <dd>{serviceFrom || serviceTo ? fmtLong(serviceFrom || serviceTo) : 'To be confirmed'}</dd>
              </div>
            ) : (
              <>
                <div><dt>Service Date From:</dt><dd>{fmtLong(serviceFrom)}</dd></div>
                <div><dt>Service Date To:</dt><dd>{fmtLong(serviceTo)}</dd></div>
              </>
            )}
          </dl>
        </div>

        <div className="isd-inv-parties">
          <div>
            <h5>Company details</h5>
            <p><b>Name:</b> {company.name || '—'}</p>
            <p><b>ABN:</b> {company.abn || '—'}</p>
            <p><b>Address:</b> {company.address || '—'}</p>
            <p><b>Phone:</b> {company.phone || '—'}</p>
            <p><b>Email:</b> {company.email || '—'}</p>
          </div>
          <div>
            <h5>Client details</h5>
            <p><b>Name:</b> {client.name || '—'}</p>
            <p><b>Address:</b> {client.address || '—'}</p>
            {data.project?.name || data.project?.code ? (
              <p>
                <b>Project:</b> {[data.project.name, data.project.code].filter(Boolean).join(' · ')}
              </p>
            ) : null}
            <p><b>Project Address:</b> {client.projectAddress || '—'}</p>
            {data.project?.attention ? <p><b>Attention:</b> {data.project.attention}</p> : null}
            {data.project?.purchaseOrderNumber ? (
              <p><b>PO Number:</b> {data.project.purchaseOrderNumber}</p>
            ) : null}
            <p><b>Phone:</b> {client.phone || '—'}</p>
            <p><b>Email:</b> {client.email || '—'}</p>
          </div>
        </div>

        <div className="isd-inv-services">
          <h5>Service details</h5>
          <table>
            <thead>
              <tr><th>Service / Task</th><th>Description</th><th className="r">Amount</th></tr>
            </thead>
            {lines.map((l, i) => (
              <tbody key={l.id || i} className="isd-svc">
                <tr className="isd-svc-row">
                  <td colSpan={2}><b>{l.name}</b></td>
                  <td className="r"><b>{money(l.amount)}</b></td>
                </tr>
                {(l.groups || []).map((g) => (
                  <React.Fragment key={g.name}>
                    <tr className="isd-group-row"><td colSpan={3}>{g.name}</td></tr>
                    {g.tasks.map((t, ti) => (
                      <tr key={`${g.name}-${ti}`} className="isd-task-row">
                        <td>{t.title}</td>
                        <td className="desc">{t.scope || '—'}</td>
                        <td className="r">{t.amount != null ? money(t.amount) : 'Included'}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            ))}
          </table>
          <button
            type="button"
            className="isd-link-btn"
            onClick={onAddLine}
            disabled={!onAddLine}
            title={
              onAddLine
                ? 'Describe the extra line in Notes and set the new Amount'
                : "Template lines come from the project's services"
            }
          >
            + Add line item
          </button>
        </div>

        <div className="isd-inv-totals">
          <div><span>Subtotal (ex. GST)</span><b>{money(subtotal)}</b></div>
          <div><span>GST ({Math.round(gstRate * 100)}%)</span><span>{money(gst)}</span></div>
          <div className="grand">
            <div>
              <b>Grand Total (inc. GST)</b>
              <small>Subtotal {money(subtotal)} + GST {money(gst)}</small>
            </div>
            <strong>{money(total)}</strong>
          </div>
        </div>

        <div className="isd-inv-note">
          Your payment is due within {dueDays} days of the invoice date. Thank you for your business.
        </div>

        <div className="isd-inv-bank">
          <h5>Bank details</h5>
          {[
            ['Bank name', bank.bankName],
            ['Account name', bank.accountName],
            ['BSB', bank.bsb],
            ['Account number', bank.accountNumber],
            ['Reference', 'Use invoice reference no'],
          ].map(([k, v]) => (
            <div key={k} className="row"><span>{k}</span><b>{v || '—'}</b></div>
          ))}
        </div>

        <div className="isd-inv-foot">
          <p>
            {data.footer?.text ||
              `© ${new Date().getFullYear()} ${company.name || 'Company'}. All rights reserved.`}
          </p>
          {(() => {
            const email = data.footer ? data.footer.email : company.email;
            const phone = data.footer ? data.footer.phone : company.phone;
            const parts = [email && `Email: ${email}`, phone && `Phone: ${phone}`].filter(Boolean);
            return parts.length ? <p>{parts.join(' · ')}</p> : null;
          })()}
          {data.footer?.address ? <p>{data.footer.address}</p> : null}
          {data.footer?.poweredBy === false ? null : (
            <p className="isd-inv-powered">Powered by SAS Cleaning Suite</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ───────────── edit single invoice drawer ───────────── */
function EditInvoiceDrawer({ date, serviceTo, existing, defaults, invoiceData, projectTitle, onClose, onSave, onSkip, onRestore }) {
  const [amount, setAmount] = useState(existing?.amount ?? defaults.total.toFixed(2));
  const [sendDate, setSendDate] = useState(existing?.sendDate || date);
  const [dueDays, setDueDays] = useState(existing?.dueDays ?? defaults.dueDays);
  const [notes, setNotes] = useState(existing?.notes || '');
  const notesRef = useRef(null);
  const skipped = existing?.status === 'skipped';

  return createPortal(
    <div className="isd-overlay isd-overlay--top" onClick={(e) => e.target === e.currentTarget && onClose()} role="presentation">
      <aside className="isd-drawer" role="dialog" aria-modal="true" aria-label="Edit invoice">
        <header className="isd-head">
          <div>
            <h2>Edit Invoice,</h2>
            <p>{projectTitle}</p>
          </div>
          <button type="button" className="isd-close" onClick={onClose} aria-label="Close"><FaTimes /></button>
        </header>

        <div className="isd-body">
          <div className="isd-edit-banner">
            <strong>Invoice for {fmtShort(date)}</strong>
            <span className={`isd-pill ${skipped ? 'skipped' : 'scheduled'}`}>{skipped ? 'Skipped' : 'Scheduled'}</span>
          </div>

          <div className="isd-edit-form">
            <p className="isd-muted strong">This invoice only — changes won&apos;t affect the rest of the series.</p>
            <div className="isd-grid2">
              <label className="isd-field">
                <span className="isd-label">Amount</span>
                <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </label>
              <label className="isd-field">
                <span className="isd-label">Send date</span>
                <input type="date" value={sendDate} onChange={(e) => setSendDate(e.target.value)} />
              </label>
            </div>
            <label className="isd-field">
              <span className="isd-label">Due date override (days after invoice)</span>
              <input type="number" min="0" value={dueDays} onChange={(e) => setDueDays(Number(e.target.value))} />
            </label>
            <label className="isd-field">
              <span className="isd-label">Notes / line adjustments</span>
              <textarea ref={notesRef} rows={3} value={notes} placeholder="e.g. Added extra service, adjusted discount…" onChange={(e) => setNotes(e.target.value)} />
            </label>
          </div>

          <InvoicePreview
            data={invoiceData}
            invoiceDate={sendDate}
            dueDays={Number(dueDays) || 0}
            serviceFrom={date}
            serviceTo={serviceTo || date}
            overrideTotal={amount}
            onEdit
            onAddLine={() => notesRef.current?.focus()}
          />
        </div>

        <footer className="isd-foot">
          {skipped ? (
            <button type="button" className="isd-btn isd-btn--warn" onClick={() => onRestore(date)}>Restore invoice</button>
          ) : (
            <button type="button" className="isd-btn isd-btn--warn" onClick={() => onSkip(date)}>Skip this invoice</button>
          )}
          <button
            type="button"
            className="isd-btn isd-btn--primary"
            onClick={() => onSave(date, { status: 'edited', amount, sendDate, dueDays: Number(dueDays) || 0, notes })}
          >
            Save changes
          </button>
        </footer>
      </aside>
    </div>,
    document.body,
  );
}

/* ───────────── calendar ───────────── */
function InvoiceCalendar({ schedule, statusFor, onPick, initialMonth, movedTo = {} }) {
  const [cursor, setCursor] = useState(() => {
    const d = initialMonth ? parseISO(initialMonth) : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  const set = new Set(schedule);

  return (
    <div className="isd-cal">
      <div className="isd-cal-nav">
        <button type="button" onClick={() => setCursor(new Date(year, month - 1, 1))} aria-label="Previous month">‹</button>
        <b>{MONTHS[month].slice(0, 3)} {year}</b>
        <button type="button" onClick={() => setCursor(new Date(year, month + 1, 1))} aria-label="Next month">›</button>
      </div>
      <div className="isd-cal-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="dow">{d}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const iso = `${year}-${pad(month + 1)}-${pad(d)}`;
          // A schedule date, or the day an edited invoice was moved to.
          const original = movedTo[iso];
          const marked = set.has(iso) || Boolean(original);
          const status = set.has(iso) ? statusFor(iso) : original ? 'scheduled' : null;
          return (
            <button
              key={iso}
              type="button"
              className={`day ${marked ? 'marked' : ''}`}
              disabled={!marked || status === 'sent'}
              title={original && !set.has(iso) ? `Moved from ${fmtShort(original)}` : undefined}
              onClick={() => marked && onPick(set.has(iso) ? iso : original)}
            >
              <span>{d}</span>
              {marked ? <i className={`dot ${status}`} /> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ───────────── main drawer ───────────── */
const FREQS = [
  ['weekly', 'Weekly'],
  ['fortnightly', 'Fortnightly'],
  ['monthly', 'Monthly'],
  ['custom', 'Custom'],
];
const PAYMENTS = [
  ['bank_transfer', 'Bank Transfer', '🏦'],
  ['stripe', 'Stripe', '💜'],
  ['manual', 'Manual / Other', '✍️'],
];

const PAY_METHODS = `
  query InvoicePayMethods {
    invoicePayMethods {
      stripeReady
      bankReady
      bankName
      accountName
      bsb
      accountNumber
      reference
    }
  }
`;
const ATTACH = [
  ['pdf', 'PDF Attachment'],
  ['portal', 'In-portal Link'],
  ['both', 'Both'],
];
const ATTACH_NOTE = {
  pdf: 'Client receives an email with the invoice attached as a PDF.',
  portal: 'Client receives an email with a link to view the invoice in the portal.',
  both: 'Client receives an email with PDF attached and a link to view in portal.',
};

const DEFAULTS = {
  projectType: 'one_time',
  frequency: 'weekly',
  every: 10,
  unit: 'days',
  startDate: '',
  endDate: '',
  ongoing: false,
  dueDays: 15,
  paymentMode: 'bank_transfer',
  attachAs: 'both',
  overrides: {},
};

/** Stable default so the re-seed effect does not fire on every render. */
const NO_SETTINGS = {};

export default function InvoiceSettingsDrawer({
  open,
  onClose,
  onSave,
  saving = false,
  projectTitle = '',
  projectCode = '',
  initialSettings = NO_SETTINGS,
  sentDates = [],
  invoiceData = { lines: [] },
  /** From the project: { projectType, frequency, frequencyLocked, startDate, endDate, datesFrom, label } */
  profile = null,
}) {
  const [s, setS] = useState({ ...DEFAULTS, ...initialSettings });
  const [editingDate, setEditingDate] = useState(null);
  const [methods, setMethods] = useState(null);

  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setS({ ...DEFAULTS, ...initialSettings });
      setError('');
    }
  }, [open, initialSettings]);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    graphqlRequest(PAY_METHODS)
      .then((res) => live && setMethods(res?.invoicePayMethods || null))
      .catch(() => live && setMethods(null));
    return () => {
      live = false;
    };
  }, [open]);

  const ready = {
    bank_transfer: methods ? methods.bankReady : true,
    stripe: methods ? methods.stripeReady : false,
    manual: true,
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && !editingDate && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, editingDate, onClose]);

  const set = (patch) => setS((prev) => ({ ...prev, ...patch }));
  const recurring = s.projectType === 'recurring';

  const schedule = useMemo(
    () => (recurring ? buildSchedule(s) : []),
    [recurring, s.startDate, s.endDate, s.ongoing, s.frequency, s.every, s.unit], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const sentSet = useMemo(() => new Set(sentDates), [sentDates]);
  const statusFor = (iso) => {
    if (sentSet.has(iso)) return 'sent';
    const o = s.overrides[iso];
    if (o?.status === 'skipped') return 'skipped';
    if (o?.status === 'edited') return 'edited';
    return 'scheduled';
  };
  const sentCount = schedule.filter((d) => sentSet.has(d)).length;
  // New send day → original schedule date, for edited invoices that were moved.
  const movedTo = {};
  schedule.forEach((date) => {
    const o = s.overrides[date];
    if (o?.status === 'edited' && o.sendDate && o.sendDate !== date && !sentSet.has(date)) {
      movedTo[o.sendDate] = date;
    }
  });

  const subtotal = (invoiceData.lines || []).reduce((a, l) => a + Number(l.amount || 0), 0);
  const defaultTotal = subtotal * (1 + (invoiceData.gstRate ?? 0.1));

  const today = toISO(new Date());
  const firstDate = recurring ? schedule[0] : null;
  const previewDate = (firstDate && (s.overrides[firstDate]?.sendDate || firstDate)) || today;
  const periodEnd = (date) => {
    const i = schedule.indexOf(date);
    if (i >= 0 && schedule[i + 1]) {
      return toISO(new Date(parseISO(schedule[i + 1]).getTime() - 86400000));
    }
    return s.endDate || date;
  };
  // Service date comes from the schedule or the project start, never "today".
  const previewFrom = firstDate || (recurring ? '' : profile?.startDate || '');
  const previewTo = firstDate ? periodEnd(firstDate) : '';
  const ongoing = recurring && Boolean(s.ongoing);
  const typeLocked = Boolean(profile?.projectType);
  const freqLocked = recurring && Boolean(profile?.frequencyLocked);
  const datesLocked =
    recurring && Boolean(profile?.startDate && (profile?.endDate || profile?.ongoing));

  const setOverride = (date, patch) =>
    set({ overrides: { ...s.overrides, [date]: { ...(s.overrides[date] || {}), ...patch } } });

  const handleSave = () => {
    if (recurring && (!s.startDate || (!s.endDate && !ongoing))) {
      setError(
        'Set the start date, and an end date or mark the project Ongoing on the project page.',
      );
      return;
    }
    if (s.paymentMode === 'stripe' && methods && !methods.stripeReady) {
      setError('Connect Stripe in Settings before choosing it here.');
      return;
    }
    if (s.paymentMode === 'bank_transfer' && methods && !methods.bankReady) {
      setError('Add the bank name, BSB and account number in Settings before choosing bank transfer.');
      return;
    }
    setError('');
    const payload = {
      projectType: s.projectType,
      frequency: recurring ? s.frequency : null,
      every: recurring && s.frequency === 'custom' ? Number(s.every) || 1 : null,
      unit: recurring && s.frequency === 'custom' ? s.unit : null,
      startDate: recurring ? s.startDate : null,
      endDate: recurring && !ongoing ? s.endDate : null,
      ongoing,
      dueDays: Number(s.dueDays) || 0,
      paymentMode: s.paymentMode,
      attachAs: s.attachAs,
      overrides: recurring ? s.overrides : {},
    };
    onSave?.(payload);
  };

  if (!open) return null;

  return createPortal(
    <>
      <div className="isd-overlay" onClick={(e) => e.target === e.currentTarget && !saving && onClose()} role="presentation">
        <aside className="isd-drawer" role="dialog" aria-modal="true" aria-label="Invoice settings">
          <header className="isd-head">
            <div>
              <h2>Invoice Settings</h2>
              <p>{projectTitle}{projectCode ? ` · ${projectCode}` : ''}</p>
            </div>
            <button type="button" className="isd-close" onClick={onClose} aria-label="Close" disabled={saving}><FaTimes /></button>
          </header>

          <div className="isd-body">
            {/* project type */}
            <section className="isd-section">
              <div className="isd-section-title"><span>Project type</span></div>
              <div className="isd-toggle">
                <button
                  type="button"
                  className={!recurring ? 'on' : ''}
                  disabled={typeLocked && profile.projectType !== 'one_time'}
                  title={typeLocked && profile.projectType !== 'one_time' ? 'This project has multiple visits' : undefined}
                  onClick={() => set({ projectType: 'one_time' })}
                >
                  📌 One-time visit
                </button>
                <button
                  type="button"
                  className={recurring ? 'on' : ''}
                  disabled={typeLocked && profile.projectType !== 'recurring'}
                  title={typeLocked && profile.projectType !== 'recurring' ? 'This project is a one-time visit' : undefined}
                  onClick={() => set({ projectType: 'recurring' })}
                >
                  🔁 Multiple time visit
                </button>
              </div>
              {typeLocked ? (
                <p className="isd-lock-note">
                  Set by the project ({profile.label}). Change the project&apos;s frequency to switch.
                </p>
              ) : null}
            </section>

            {/* frequency */}
            {recurring && (
              <section className="isd-section">
                <div className="isd-section-title"><span>Frequency &amp; contract duration</span></div>
                <span className="isd-label">Billing frequency</span>
                <div className="isd-chips">
                  {FREQS.map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      className={`isd-chip ${s.frequency === key ? 'on' : ''}`}
                      disabled={freqLocked && s.frequency !== key}
                      onClick={() => set({ frequency: key })}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {s.frequency === 'custom' && (
                  <div className="isd-every">
                    <span className="isd-muted">Every</span>
                    <input type="number" min="1" value={s.every} disabled={freqLocked} onChange={(e) => set({ every: e.target.value })} />
                    <select value={s.unit} disabled={freqLocked} onChange={(e) => set({ unit: e.target.value })}>
                      <option value="days">days</option>
                      <option value="weeks">weeks</option>
                      <option value="months">months</option>
                    </select>
                  </div>
                )}
                <div className="isd-grid2">
                  <label className="isd-field">
                    <span className="isd-label">Contracted start date</span>
                    <input type="date" value={s.startDate} readOnly={datesLocked} onChange={(e) => set({ startDate: e.target.value })} />
                  </label>
                  <label className="isd-field">
                    <span className="isd-label">Contracted end date</span>
                    {ongoing ? (
                      <input type="text" value="Ongoing (until the client stops)" readOnly />
                    ) : (
                      <input type="date" min={s.startDate || undefined} value={s.endDate} readOnly={datesLocked} onChange={(e) => set({ endDate: e.target.value })} />
                    )}
                  </label>
                </div>
                {recurring && !s.startDate ? (
                  <p className="isd-lock-note">
                    This project has no start date yet. Set it on the project to build the invoice schedule.
                  </p>
                ) : null}
                {datesLocked ? (
                  <p className="isd-lock-note">
                    {ongoing
                      ? `Ongoing project: invoices run from the start date until an end date is set on the project. The calendar shows the next ${ONGOING_MONTHS} months. `
                      : ''}
                    {profile.datesFrom === 'contract'
                      ? `From contract${profile.contractNumber ? ` ${profile.contractNumber}` : ''}.`
                      : 'From the project start and end dates.'}{' '}
                    {freqLocked ? `Frequency from the project (${profile.label}).` : ''}
                  </p>
                ) : null}
              </section>
            )}

            {/* due date */}
            <section className="isd-section">
              <div className="isd-section-title"><span>⏰ Due date rule</span></div>
              <div className="isd-due">
                <span className="isd-muted">Due</span>
                <input type="number" min="0" value={s.dueDays} onChange={(e) => set({ dueDays: e.target.value })} />
                <span className="isd-muted">days after each invoice date</span>
              </div>
              <p className="isd-hint">
                {recurring
                  ? 'Applied automatically to all scheduled invoices — overridable per invoice in the calendar.'
                  : `e.g. Invoice dated 13 Sep → due ${fmtShort(toISO(new Date(2026, 8, 13 + (Number(s.dueDays) || 0)))).replace(/ 2026$/, '')}`}
              </p>
            </section>

            {/* payment */}
            <section className="isd-section">
              <div className="isd-section-title"><span>Payment mode</span></div>
              <div className="isd-pay-grid">
                {PAYMENTS.map(([key, label, icon]) => (
                  <button
                    key={key}
                    type="button"
                    className={`isd-pay ${s.paymentMode === key ? 'on' : ''}`}
                    disabled={!ready[key]}
                    onClick={() => ready[key] && set({ paymentMode: key })}
                  >
                    <span>{icon}</span>
                    <b>{label}</b>
                    {s.paymentMode === key ? <i className="tick">✓</i> : null}
                  </button>
                ))}
              </div>
              {methods && !methods.stripeReady ? (
                <p className="isd-hint">Stripe is off until the keys are saved in Settings → Stripe Payment Settings.</p>
              ) : null}
              {methods && !methods.bankReady ? (
                <p className="isd-hint">Bank transfer needs a bank name, BSB and account number in Settings.</p>
              ) : null}
              {s.paymentMode === 'bank_transfer' && methods?.bankReady ? (
                <p className="isd-hint">
                  Clients will be shown {methods.bankName}
                  {methods.accountName ? ` · ${methods.accountName}` : ''}
                  {methods.bsb ? ` · BSB ${methods.bsb}` : ''}
                  {methods.accountNumber ? ` · ${methods.accountNumber}` : ''}.
                </p>
              ) : null}
              {s.paymentMode === 'stripe' && methods?.stripeReady ? (
                <p className="isd-hint">Clients pay this project’s invoices with the Stripe account in Settings.</p>
              ) : null}
            </section>

            {/* template */}
            <section className="isd-section">
              <InvoicePreview
                data={invoiceData}
                invoiceDate={previewDate}
                dueDays={Number(s.dueDays) || 0}
                serviceFrom={previewFrom}
                serviceTo={previewTo}
              />
            </section>

            {/* calendar */}
            {recurring && (
              <section className="isd-section">
                <div className="isd-section-title"><span>Automated invoice calendar</span></div>
                <div className="isd-cal-summary">
                  <b>
                    {ongoing
                      ? `${sentCount} sent · ongoing`
                      : `${sentCount} of ${schedule.length} invoices across contract`}
                  </b>
                  <span>
                    {s.startDate ? fmtShort(s.startDate) : '—'} —{' '}
                    {ongoing ? 'Ongoing' : s.endDate ? fmtShort(s.endDate) : '—'}
                  </span>
                </div>
                <div className="isd-legend">
                  <span><i className="dot sent" />Sent</span>
                  <span><i className="dot scheduled" />Scheduled</span>
                  <span><i className="dot skipped" />Skipped</span>
                  <span><i className="dot edited" />Edited</span>
                  <em>Click any marked date to edit</em>
                </div>
                {schedule.length === 0 ? (
                  <p className="isd-hint">
                    Set a start date and an end date (or mark the project Ongoing) to generate the invoice calendar.
                  </p>
                ) : (
                  <InvoiceCalendar
                    key={schedule[0]}
                    schedule={schedule}
                    statusFor={statusFor}
                    onPick={setEditingDate}
                    initialMonth={schedule[0]}
                    movedTo={movedTo}
                  />
                )}
              </section>
            )}

            {/* attach */}
            <section className="isd-section">
              <div className="isd-section-title"><span>Attach invoice as</span></div>
              <div className="isd-attach">
                {ATTACH.map(([key, label]) => (
                  <button key={key} type="button" className={s.attachAs === key ? 'on' : ''} onClick={() => set({ attachAs: key })}>{label}</button>
                ))}
              </div>
              <div className="isd-attach-note">👁 {ATTACH_NOTE[s.attachAs]}</div>
            </section>
          </div>

          {error ? <p className="isd-error" role="alert">{error}</p> : null}
          <footer className="isd-foot">
            <button type="button" className="isd-btn isd-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="button" className="isd-btn isd-btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </footer>
        </aside>
      </div>

      {editingDate && (
        <EditInvoiceDrawer
          date={editingDate}
          serviceTo={periodEnd(editingDate)}
          existing={s.overrides[editingDate]}
          defaults={{ total: defaultTotal, dueDays: Number(s.dueDays) || 0 }}
          invoiceData={invoiceData}
          projectTitle={`${projectTitle}${projectCode ? ` ${projectCode}` : ''}`}
          onClose={() => setEditingDate(null)}
          onSave={(date, patch) => { setOverride(date, patch); setEditingDate(null); }}
          onSkip={(date) => { setOverride(date, { status: 'skipped' }); setEditingDate(null); }}
          onRestore={(date) => {
            const next = { ...s.overrides };
            delete next[date];
            set({ overrides: next });
            setEditingDate(null);
          }}
        />
      )}
    </>,
    document.body,
  );
}